import cx from 'classnames';
import {isEmpty, xor, map, filter, forIn, without, forEach, some} from 'lodash';
import moment from 'moment-timezone';
import {Fragment, useState, useMemo, useContext, useEffect, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions, WorkBook} from 'xlsx';

import {groupApi, userApi} from 'api';
import {clientConnectApi} from 'api/client-connect';
import {
    ConfirmModal, FileInput, Button, notifier, Pagination, ClientConnectFormDialog,
    Search, ActionButton, PredefinedNotifierType, Dropdown, DropdownItem, ClientConnectMessageDialog,
    GlobalLoader, Select, DatePicker, FiltersWrapper, FiltersWrapperChildrenProps, FiltersDropdown,
    PageHeader
} from 'components';
import {APP_NAME} from 'constants/app';
import {UserContext} from 'context';
import {Choice, TaskStatus} from 'enums';
import {useApiData, usePrevious, useUserInfoSchema} from 'hooks';
import {Group, Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getGroupId, getSubgroupId, DataRequest} from 'services';
import {
    ClientConnect, CursorPaginatedDBResponse, ImportError, PaginationOptions, UserSummary, ClientConnectsFilters, ApiError
} from 'types';
import {userAuthUtils, settingsUtils, apiUtils, logger} from 'utils';
import {lazyModules} from 'utils/lazy-modules';

import {ClientConnectAccordion} from './ClientConnectAccordion';
import {ClientConnectScheduledMessagesDialog} from './ClientConnectScheduledMessagesDialog';
import {ClientConnectTable} from './ClientConnectTable';
import {ImportErrorsDialog} from './ImportErrorsDialog';
import {exportUtils} from './export.utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const EXPORT_PORTION_SIZE = 500;

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

type FormDialogProps = {
    open: boolean;
    loading: boolean;
    mode: 'create' | 'update';
    connect?: ClientConnect,
    error?: ApiError;
}

type SendMessageDialogProps = {
    open: boolean;
    connect: ClientConnect;
}

// The date range bounds the search: an unanchored regex cannot use an index, so without it every query scans the
// whole account. Widening the range is one click, and that trade is what keeps the list fast as the base grows.
// Same default period as the activity dashboard.
const DEFAULT_PERIOD_DAYS = 14;
const PERIOD_MIN_COUNT = 1000;

const defaultFilters: ClientConnectsFilters = {
    searchText: '',
    createdAfterDate: null,
    createdBeforeDate: null,
    enabled: null,
};

// Built in the account time zone, the same one DatePicker hands back, so the automatic bounds and a hand-picked
// date for the same visible day mean the same instant.
const buildDefaultPeriod = (timeZone: string) => ({
    createdAfterDate: moment.tz(timeZone).subtract(DEFAULT_PERIOD_DAYS, 'days').startOf('day'),
    // Records created later today would fall outside a range that ends at the moment the page was opened.
    createdBeforeDate: moment.tz(timeZone).endOf('day'),
});

export const ClientsConnects: React.FC = () => {
    const {t} = useTranslation();
    const [importRef, setImportRef] = useState(useRef<HTMLInputElement>(null));
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
    const [formDialogProps, setFormDialogProps] = useState<FormDialogProps>(null);
    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);

    const [loadingExportData, setLoadingExportData] = useState(false);
    const [loadingExportProgress, setLoadingExportProgress] = useState(0);
    const prevExportLoading = usePrevious(loadingExportData);
    const [itemsForExport, setItemsForExport] = useState([]);

    const {settings} = useContext(UserContext);
    const {timeZone, dateFormat, timeFormat} = useMemo(() => settings.common, [settings]);
    const {smsEnabled, emailEnabled} = useMemo(() => settingsUtils.getClientConnectionMessageAvailability(settings), [settings]);

    const downloadFileDisabled = useMemo(() => userAuthUtils.isDownloadFileDisabledAuthSource(), []);

    const [sendMessageDialogProps, setSendMessageDialogProps] = useState<SendMessageDialogProps>({
        open: false,
        connect: null,
    });
    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const [bulkDeleteConfirmationDialogOpen, setBulkDeleteConfirmationDialogOpen] = useState(false);
    const [bulkDeleteFileredConfirmationDialogOpen, setBulkDeleteFileredConfirmationDialogOpen] = useState(false);
    const [deleteScheduledMessageConfirmationDialogProps, setDeleteScheduledMessageConfirmationDialogProps] = useState({
        open: false,
        data: null
    });
    const [scheduledMessagesDialogProps, setScheduledMessagesDialogProps] = useState({
        open: false,
        updated: false,
        connect: null
    });
    const [importErrorsDialogOpen, setImportErrorsDialogOpen] = useState(false);
    const [importErrors, setImportErrors] = useState(null);
    const [importedConnects, setImportedConnects] = useState(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [toggleDisabledInProgress, setToggleDisabledInProgress] = useState([]);
    // Held in state rather than as a constant: once the period is armed it becomes the default the Clear button
    // resets to, and what FiltersDropdown compares against to decide whether any filter is applied at all.
    const [currentDefaultFilters, setCurrentDefaultFilters] = useState(defaultFilters);
    const [formFilters, setFormFilters] = useState(defaultFilters);
    const periodChecked = useRef(false);

    const enabledSelectOptions: Option<Choice>[] = useMemo(() => [
        {id: Choice.Yes, name: t(COMMON_T_KEYS.ALL.YES)},
        {id: Choice.No, name: t(COMMON_T_KEYS.ALL.NO)},
    ], [t]);

    const startIndex = useMemo(() => (pagination.page - 1) * pagination.pageSize, [pagination]);

    const loadClientsConnectsFn = useCallback(() => clientConnectApi.getAll(formFilters, pagination),
        [formFilters, pagination]);

    const onSuccessLoadConnects = useCallback(({pages, page, count}) => {
        // The period exists to keep a later search inside it, which only pays off on an account big enough for
        // an unbounded search to hurt. The first load runs without it on purpose: measured inside the period the
        // count would report recent activity, not the size of the collection a search has to walk, and an
        // account with half a million old records but a quiet fortnight would end up unbounded. Listing without
        // a period is cheap either way — it is served by the account index and stops at the page limit.
        if (!periodChecked.current) {
            periodChecked.current = true;
            if (count >= PERIOD_MIN_COUNT) {
                const period = buildDefaultPeriod(timeZone);
                setCurrentDefaultFilters((prev) => ({...prev, ...period}));
                setFormFilters((prev) => ({...prev, ...period}));
                return;
            }
        }
        if (!pages && pagination.page !== defaultPagination.page) {
            setPagination((prev) => ({...prev, page: defaultPagination.page}));
        } else if (pages && page > pages) {
            setPagination((prev) => ({...prev, page: pages}));
        }
    }, [pagination, timeZone]);

    // The list keeps the previous results when a request fails, so without a toast a failed search reads as a
    // successful one that returned the wrong rows.
    const onErrorLoadConnects = useCallback((error) => {
        if (apiUtils.isTooManyRequestsError(error)) {
            notifier.error({title: t(T_KEYS.ERROR.TOO_MANY_REQUESTS)});
        } else if (apiUtils.isValidationError(error)) {
            notifier.error({title: t(T_KEYS.ERROR.VALIDATION)});
        } else {
            notifier.error({title: t(T_KEYS.ERROR.UNEXPECTED)});
        }
    }, [t]);

    const {
        send: loadConnects,
        data: clientsConnectsResponse,
        loading
    } = useApiData(loadClientsConnectsFn, {onSuccess: onSuccessLoadConnects, onError: onErrorLoadConnects});

    const resetConfirmBulkDeleteFilteredDialog = useCallback(() => {
        setBulkDeleteFileredConfirmationDialogOpen(false);
    }, []);

    const onSuccessBulkDeleteFiltered = useCallback(() => {
        loadConnects();
    }, [loadConnects]);

    const {send: bulkDeleteFilteredConnects} = useApiData(clientConnectApi.bulkDeleteFiltered,
        {onSuccess: onSuccessBulkDeleteFiltered, notificationType: PredefinedNotifierType.Delete});

    const totalCount = useMemo(() => clientsConnectsResponse?.count, [clientsConnectsResponse]);
    const connects = useMemo(() => clientsConnectsResponse?.items, [clientsConnectsResponse]);
    const displayedConnectsCount = useMemo(() => connects?.length, [connects]);

    const {send: loadGroups} = useApiData(groupApi.getGroups, {onSuccess: setGroups});
    const {send: loadAgentUsers, data: agentUsers = []} = useApiData<UserSummary[]>(userApi.getAgentUsersSummary);
    const {fetchUserInfoSchema, getUserInfoFields, userInfoSchemaData} = useUserInfoSchema();

    const dataRequest = useMemo(() => new DataRequest<CursorPaginatedDBResponse<ClientConnect>>({
        mergeResults: (acc, current) => [...(acc || []), ...current.results],
        formatResponse: (data) => ({
            status: !data.hasNext ? TaskStatus.Completed : TaskStatus.InProgress,
            progress: Math.round(loadingExportProgress + data.results.length / totalCount * 100),
            data
        }),
        onRequest: (data) => clientConnectApi.getChunkData(
            formFilters,
            {next: data?.next, limit: EXPORT_PORTION_SIZE}
        ),
        onProgress: setLoadingExportProgress,
        onSuccess: setItemsForExport,
        onError: () => {
            notifier.error({title: t(T_KEYS.ERROR.UNEXPECTED)});
        },
        onComplete: () => {
            setLoadingExportData(false);
            setLoadingExportProgress(0);
        }
    }), [loadingExportProgress, totalCount, formFilters, t]);

    const updateScheduledMessagesDialogProps = useCallback(({open, updated, connect}) => {
        setScheduledMessagesDialogProps({open, updated, connect});
    }, []);

    const resetScheduledMessagesDialog = useCallback((data: {reload: boolean}) => {
        if (data.reload) {
            loadConnects();
        }
        updateScheduledMessagesDialogProps({open: false, updated: false, connect: null});
    }, [loadConnects, updateScheduledMessagesDialogProps]);

    const resetConfirmScheduledMessageDeleteDialog = useCallback(() => {
        setDeleteScheduledMessageConfirmationDialogProps({
            open: false,
            data: null
        });
    }, []);

    const onSuccessDeleteScheduledMessage = useCallback((connect: ClientConnect) => {
        resetConfirmScheduledMessageDeleteDialog();
        if (connect.scheduledMessages.length) {
            updateScheduledMessagesDialogProps({open: true, updated: true, connect});
        } else {
            resetScheduledMessagesDialog({reload: true});
        }
    }, [resetConfirmScheduledMessageDeleteDialog, resetScheduledMessagesDialog, updateScheduledMessagesDialogProps]);

    const {send: deleteScheduledMessage} = useApiData(clientConnectApi.deleteScheduledMessage,
        {onSuccess: onSuccessDeleteScheduledMessage});

    const onSuccessCreateConnection = useCallback((connect: ClientConnect) => {
        if (!isEmpty(connect)) {
            loadConnects();
        }
        setFormDialogProps(null);
    }, [loadConnects]);

    const onErrorUpdateConnection = useCallback((error) => {
        if (apiUtils.isValidationError(error)) {
            setFormDialogProps({...formDialogProps, error});
            notifier.error({title: t(T_KEYS.ERROR.VALIDATION)});
        } else {
            notifier.error({title: t(T_KEYS.ERROR.UNEXPECTED)});
        }
    }, [formDialogProps, t]);

    const {send: createConnect} = useApiData(clientConnectApi.create, {
        onSuccess: onSuccessCreateConnection,
        onError: onErrorUpdateConnection
    });

    const onSuccessUpdateConnect = useCallback((updatedConnect: ClientConnect) => {
        if (!isEmpty(updatedConnect)) {
            loadConnects();
        }
        if (formDialogProps) {
            setFormDialogProps(null);
        }
    }, [formDialogProps, loadConnects]);

    const resetSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const onToggleSearch = useCallback(() => setIsSearchOpen(!isSearchOpen), [isSearchOpen]);

    // A whitespace-only term costs the same full scan as a real one, so it is trimmed away on submit rather than
    // while typing, where it would stop the agent from typing a space inside a name.
    const onSubmitFilters = useCallback((filters: ClientConnectsFilters) => {
        // Whatever the agent applied wins, even if the first response has not landed yet: without this the
        // period check could arm its own dates on top of a range they had just picked.
        periodChecked.current = true;
        setFormFilters({...filters, searchText: filters.searchText.trim()});
        resetSelection();
    }, [resetSelection]);

    const {send: updateConnect} = useApiData(clientConnectApi.update, {
        onSuccess: onSuccessUpdateConnect, onError: onErrorUpdateConnection, notificationType: PredefinedNotifierType.Save
    });

    const {send: setDisabled} = useApiData(clientConnectApi.setDisabled, {
        onSuccess: onSuccessUpdateConnect, onError: onErrorUpdateConnection, notificationType: PredefinedNotifierType.Save
    });

    const openImportErrorsDialog = useCallback((errors: ImportError[]) => {
        setImportErrorsDialogOpen(true);
        setImportErrors(errors);
    }, []);

    const onSuccessImportData = useCallback((data: {success: ClientConnect[], error: ImportError[]}) => {
        const {success, error} = data;
        if (!isEmpty(error)) {
            openImportErrorsDialog(error);
        } else {
            setImportedConnects([]);
        }
        if (!isEmpty(success)) {
            loadConnects();
        }
    }, [openImportErrorsDialog, loadConnects]);

    const isWarningChecker = useCallback((data: {success: ClientConnect[], error: ImportError[]}) => !isEmpty(data?.error), []);

    const notifierErrorForImport = useCallback((error) => {
        if (apiUtils.isValidationError(error)) {
            return {title: t(T_KEYS.ERROR.VALIDATION)};
        } else {
            return {title: t(T_KEYS.ERROR.UNEXPECTED)};
        }
    }, [t]);

    const {send: importConnects} = useApiData(clientConnectApi.bulkCreate, {
        onSuccess: onSuccessImportData,
        buildNotifierErrorProps: notifierErrorForImport,
        notificationType: PredefinedNotifierType.Save,
        isWarningChecker
    });

    const isAllSelected = useMemo(() =>
        selectedIds.length === displayedConnectsCount && displayedConnectsCount > 0, [displayedConnectsCount, selectedIds.length]);

    const selectedConnects = useMemo(() =>
        filter(connects, ((connect) => selectedIds.includes(connect.id))), [connects, selectedIds]);

    const templateHeaderNames = useMemo(() => [
        t(T_KEYS.FIELDS.ID),
        t(T_KEYS.FIELDS.FIRST_NAME),
        t(T_KEYS.FIELDS.LAST_NAME),
        t(T_KEYS.FIELDS.GROUP),
        t(T_KEYS.FIELDS.SUBGROUP),
        t(COMMON_T_KEYS.SESSION.LABELS.AGENT),
        t(T_KEYS.FIELDS.EMAIL),
        t(T_KEYS.FIELDS.CONTACTS_EMAIL),
        t(T_KEYS.FIELDS.PHONE),
        t(T_KEYS.FIELDS.CONTACTS_PHONE),
        t(T_KEYS.FIELDS.COMMENTS),
    ], [t]);

    const handleCreateClick = useCallback(() => {
        setFormDialogProps({
            open: true,
            loading: false,
            mode: 'create'
        });
    }, []);

    const handleUpdateClick = useCallback((connect: ClientConnect) => {
        setFormDialogProps({
            open: true,
            loading: false,
            mode: 'update',
            connect
        });
    }, []);

    const handleSendMessageClick = useCallback((connect: ClientConnect) => {
        setSendMessageDialogProps({open: true, connect});
    }, []);

    const handleCloseSendMessageDialog = useCallback((success: boolean, data: {connect: ClientConnect}) => {
        setSendMessageDialogProps({open: false, connect: null});
        if (success && !isEmpty(data?.connect)) {
            loadConnects();
        }
    }, [loadConnects]);

    const handleDeleteClick = useCallback((connect: ClientConnect) => {
        setDeleteCandidate(connect);
        setDeleteConfirmationDialogOpen(true);
    }, []);

    const handleDeleteScheduledMessageClick = useCallback((connectId: string, messageId: string) => {
        setDeleteScheduledMessageConfirmationDialogProps({
            open: true,
            data: {connectId, messageId}
        });
    }, []);

    const handleDeleteScheduledMessageConfirmationDialogClose = useCallback(async (
        confirmed: boolean,
        data: {connectId: string, messageId: string}) => {
        if (confirmed) {
            try {
                await deleteScheduledMessage(data.connectId, data.messageId);
            } catch (error) {
                notifier.error({title: t(T_KEYS.ERROR.UNEXPECTED)});
            }
        } else {
            resetConfirmScheduledMessageDeleteDialog();
        }
    }, [deleteScheduledMessage, resetConfirmScheduledMessageDeleteDialog, t]);

    const handleOpenScheduledMessageDetailsDialog = useCallback((connect: ClientConnect) => {
        updateScheduledMessagesDialogProps({open: true, updated: false, connect});
    }, [updateScheduledMessagesDialogProps]);

    const onCloseClientConnectScheduleMessageClick = useCallback(() => {
        resetScheduledMessagesDialog({reload: scheduledMessagesDialogProps.updated});
    }, [resetScheduledMessagesDialog, scheduledMessagesDialogProps.updated]);

    const handleToggleSelection = useCallback((id: string) => {
        const updatedSelectedIds = xor(selectedIds, [id]);
        setSelectedIds(updatedSelectedIds);
    }, [selectedIds]);

    const handleToggleSelectionAll = useCallback((value?: boolean) => {
        setSelectedIds(value ?? !isAllSelected ? connects.map((c) => c.id) : []);
    }, [isAllSelected, connects]);

    const updatePagination = useCallback((updates: Partial<PaginationOptions>) => {
        setPagination((prev) => ({...prev, ...updates}));
        handleToggleSelectionAll(false);
    }, [handleToggleSelectionAll]);

    const setPage = useCallback((value: number) => {
        updatePagination({page: value});
    }, [updatePagination]);

    const setPageSize = useCallback((value: number) => {
        updatePagination({pageSize: value});
    }, [updatePagination]);

    const handleBulkDeleteClick = useCallback(() => {
        setBulkDeleteConfirmationDialogOpen(true);
    }, []);

    const resetConfirmBulkDeleteDialog = useCallback(() => {
        setBulkDeleteConfirmationDialogOpen(false);
    }, []);

    const {send: bulkDeleteConnects} = useApiData(clientConnectApi.bulkDelete,
        {onSuccess: loadConnects, notificationType: PredefinedNotifierType.Delete});

    const handleBulkFilteredDeleteClick = useCallback(() => {
        setBulkDeleteFileredConfirmationDialogOpen(true);
    }, []);

    const handleBulkDeleteFiltredItemsConfirmationDialogClose = useCallback((confirmed: boolean) => {
        resetConfirmBulkDeleteFilteredDialog();
        if (confirmed) {
            bulkDeleteFilteredConnects(formFilters);
        }
    }, [resetConfirmBulkDeleteFilteredDialog, bulkDeleteFilteredConnects, formFilters]);

    const handleBulkDeleteConfirmationDialogClose = useCallback((confirmed: boolean,) => {
        resetConfirmBulkDeleteDialog();
        if (confirmed) {
            bulkDeleteConnects(selectedIds);
        }
    }, [resetConfirmBulkDeleteDialog, bulkDeleteConnects, selectedIds]);

    const handleImportErrorsDialogClose = useCallback(() => {
        setImportErrorsDialogOpen(false);
        setImportErrors(null);
        setImportedConnects([]);
    }, []);

    const handleFormDialogSubmit = useCallback(async (confirm: boolean, connect: Partial<ClientConnect>) => {
        if (!confirm) {
            setFormDialogProps(null);
            return;
        }

        setFormDialogProps({...formDialogProps, loading: true});
        if (formDialogProps.mode === 'create') {
            await createConnect(connect);
        } else {
            await updateConnect(connect.id, connect);
        }
    }, [formDialogProps, createConnect, updateConnect]);

    const resetConfirmDeleteDialog = useCallback(() => {
        setDeleteCandidate(null);
        setDeleteConfirmationDialogOpen(false);
    }, []);

    const {send: deleteConnect} = useApiData(clientConnectApi.deleteOne,
        {onSuccess: loadConnects, notificationType: PredefinedNotifierType.Delete});

    const handleDeleteConfirmationDialogClose = useCallback((confirm: boolean, connect: ClientConnect) => {
        resetConfirmDeleteDialog();
        if (confirm) {
            deleteConnect(connect.id);
        }
    }, [deleteConnect, resetConfirmDeleteDialog]);

    const getHeaderNames = useCallback((workbook: WorkBook) => {
        const first_sheet_name = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[first_sheet_name];
        const names = [];
        forIn(worksheet, ({v: value}, cellName) => {
            if (cellName.length === 2 && cellName.endsWith('1')) {
                names.push(value);
            }
        });
        return names;
    }, []);

    const validateImportTemplate = useCallback((workbook: WorkBook) => {
        const headerNames = getHeaderNames(workbook);
        return templateHeaderNames.every((name) => headerNames.includes(name));
    }, [templateHeaderNames, getHeaderNames]);

    const fromExcelToConnect = useCallback((row: Record<string, string>): Partial<ClientConnect> => {
        const groupName = row[t(T_KEYS.FIELDS.GROUP)];
        const groupId = getGroupId(groupName, groups);
        const fields = getUserInfoFields({groupId});
        const userInfo = exportUtils.extractUserInfo(row, fields, t);
        return {
            userInfo,
            assignmentInfo: {
                groupId,
                subgroupId: getSubgroupId(groupName, row[t(T_KEYS.FIELDS.SUBGROUP)], groups),
            },
            agent: {
                email: row[t(COMMON_T_KEYS.SESSION.LABELS.AGENT)]
            },
            contacts: {
                email: row[t(T_KEYS.FIELDS.CONTACTS_EMAIL)] || null,
                phone: row[t(T_KEYS.FIELDS.CONTACTS_PHONE)] || null
            },
            comments: row[t(T_KEYS.FIELDS.COMMENTS)],
            disabled: row[t(T_KEYS.FIELDS.ENABLED)] !== t(COMMON_T_KEYS.ALL.YES)
        };
    }, [t, groups, getUserInfoFields]);

    const importData = useCallback((rows: any[]) => {
        const imported = map(rows, (row) => fromExcelToConnect(row));
        importConnects(imported);
        setImportedConnects(imported);
    }, [fromExcelToConnect, importConnects]);

    const handleUploadImportFile = useCallback(async (buffer: ArrayBuffer) => {
        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        try {
            const data = new Uint8Array(buffer);
            const workbook = xlsx.read(data, {type: 'array'});
            const sheetsList = workbook.SheetNames;
            if (validateImportTemplate(workbook)) {
                const json = xlsx.utils.sheet_to_json(workbook.Sheets[sheetsList[0]], {raw: false});
                importData(json);
            } else {
                notifier.error({title: t(T_KEYS.ERROR.INVALID_TEMPLATE)});
            }
        } catch (error: any) {
            logger.error(error);
        }
    }, [importData, validateImportTemplate, t]);

    const openImportFileInput = useCallback(() => {
        if (importRef?.current) {
            importRef.current.click();
        }
    }, [importRef]);

    const exportData = useCallback(async (sheetName: string, fileName: string, data: ClientConnect[] = []) => {
        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const isTemplate = isEmpty(data);

        const wb = xlsx.utils.book_new();

        const writeOpts: WritingOptions = {bookType: 'xlsx', bookSST: true, type: 'binary', sheet: sheetName};

        try {

            const {userInfoFields, controlOnlyFields} = exportUtils.getFullUserInfoFieldLabels(userInfoSchemaData, t);
            const header = exportUtils.getHeader(t, {isTemplate, userInfoFields, controlOnlyFields});

            const json = data.map((connect) =>
                exportUtils.exportConnect(t, connect, {userInfoFields, controlOnlyFields}, groups, userInfoSchemaData)
            );

            const ws = xlsx.utils.json_to_sheet(json, {header});
            wb.SheetNames.push(sheetName);
            wb.Sheets[sheetName] = ws;
            wb.Props = {Author: APP_NAME};

            xlsx.writeFile(wb, fileName, writeOpts);
        } catch (error: any) {
            notifier.error({title: t(T_KEYS.EXPORT.ERROR)});
        }
    }, [userInfoSchemaData, t, groups]);

    const handleDownloadImportTemplate = useCallback(() => {
        exportData(
            t(T_KEYS.IMPORT_TEMPLATE.SHEET_NAME),
            `${t(T_KEYS.IMPORT_TEMPLATE.FILENAME)}.xlsx`
        );
    }, [exportData, t]);

    const handleExportSelected = useCallback(() => {
        exportData(
            t(T_KEYS.EXPORT.SHEET_NAME),
            `${t(T_KEYS.EXPORT.FILENAME)}.xlsx`,
            selectedConnects
        );
    }, [selectedConnects, exportData, t]);

    const handleExportAll = useCallback(() => {
        setLoadingExportData(true);
        setLoadingExportProgress(0);
        dataRequest.execute();
    }, [dataRequest]);

    const handleToggleDisabled = useCallback(async (connect: ClientConnect) => {
        try {
            setToggleDisabledInProgress([...toggleDisabledInProgress, connect.id]);
            await setDisabled(connect.id, !connect.disabled);
        } catch {
            notifier.error({title: t(T_KEYS.ERROR.UNEXPECTED)});
        } finally {
            setToggleDisabledInProgress(without(toggleDisabledInProgress, connect.id));
        }
    }, [toggleDisabledInProgress, setDisabled, t]);

    const isSendMessageAvailable = useCallback((connect: ClientConnect) => {
        const sms = connect.contacts?.phone && smsEnabled;
        const email = connect.contacts?.email && emailEnabled;
        return sms || email;
    }, [smsEnabled, emailEnabled]);

    useEffect(() => {
        if (!loadingExportData && prevExportLoading && !isEmpty(itemsForExport)) {
            exportData(
                t(T_KEYS.EXPORT.SHEET_NAME),
                `${t(T_KEYS.EXPORT.FILENAME)}.xlsx`,
                itemsForExport
            );
        }
    }, [loadingExportData, prevExportLoading, itemsForExport, exportData, t]);

    useEffect(() => () => {
        dataRequest.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        forEach(selectedIds, (id) => {
            if (!some(connects, {id})) {
                handleToggleSelection(id);
            }
        });
    }, [connects, handleToggleSelection, selectedIds]);

    useEffect(() => {
        loadConnects();
    }, [loadConnects, pagination.page]);

    useEffect(() => {
        loadAgentUsers();
        loadGroups();
        fetchUserInfoSchema();
    }, [loadAgentUsers, loadGroups, fetchUserInfoSchema]);

    const selectionButtons = useMemo(() => (
        <Fragment>
            <Button
                width='small'
                icon='arrow-up'
                className='-sm:tw-w-1/2'
                disabled={downloadFileDisabled}
                onClick={handleExportSelected}
            >
                {t(T_KEYS.BUTTONS.EXPORT)}
            </Button>
            <ActionButton fontSize='normal' action='delete' className='tw-ml-2 -sm:tw-w-1/2' onClick={handleBulkDeleteClick}>
                {t(T_KEYS.BUTTONS.DELETE)}
            </ActionButton>
        </Fragment>
    ), [downloadFileDisabled, handleExportSelected, handleBulkDeleteClick, t]);

    const hasSelection = useMemo(() => !isEmpty(selectedIds), [selectedIds]);

    const renderConfirmBulkDeleteModalContent = useCallback((itemsToDelete: number) => (
        <div>
            <span className='tw-ts-base-bold'>{itemsToDelete}</span>
            {` ${t(T_KEYS.CONFIRM_BULK_DELETE_DIALOG.NUMBER_OF_ITEMS)}`}
        </div>
    ), [t]);

    const clientConnectViewProps = useMemo(() => ({
        connects: connects || [],
        startIndex,
        selectedIds,
        isAllSelected,
        loading,
        groups,
        smsEnabled,
        emailEnabled,
        toggleDisabledInProgress,
        users: agentUsers,
        isSendMessageAvailable,
        onSendMessage: handleSendMessageClick,
        onToggleDisabled: handleToggleDisabled,
        onUpdate: handleUpdateClick,
        onDelete: handleDeleteClick,
        onOpenScheduledMessageDetailsDialog: handleOpenScheduledMessageDetailsDialog,
        onToggleSelection: handleToggleSelection,
        onToggleSelectionAll: handleToggleSelectionAll
    }), [connects, startIndex, selectedIds, isAllSelected, loading, groups, smsEnabled,
        emailEnabled, toggleDisabledInProgress, agentUsers, isSendMessageAvailable, handleSendMessageClick,
        handleToggleDisabled, handleUpdateClick, handleDeleteClick, handleOpenScheduledMessageDetailsDialog,
        handleToggleSelection, handleToggleSelectionAll]);

    return (
        <Fragment>
            {loadingExportData && <GlobalLoader value={loadingExportProgress} />}
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />

            {/* Extra bottom margin leaves room for the period caption, which hangs below the row out of the flow. */}
            <div className='tw-flex tw-mb-8 md:tw-mt-8 tw-items-center'>
                <FiltersWrapper
                    defaultFilters={currentDefaultFilters}
                    filters={formFilters}
                    onSubmit={onSubmitFilters}
                >
                    {({filters, onFiltersChange, applyFilters, ...rest}: FiltersWrapperChildrenProps<ClientConnectsFilters>) => (
                        <Fragment>
                            <div className={cx('tw-relative -lg:tw-order-2 lg:tw-w-1/2', {
                                'tw-w-full': isSearchOpen,
                                'tw-ml-1': !isSearchOpen
                            })}
                            >
                                <Search
                                    className='tw-relative'
                                    label={t(T_KEYS.SEARCH_LABEL)}
                                    open={isSearchOpen}
                                    value={filters.searchText}
                                    onChange={(value) => onFiltersChange({searchText: value})}
                                    onSubmit={applyFilters}
                                    onClick={onToggleSearch}
                                />
                                {/* The pickers live in the dropdown, so without this the applied period would be
                                    invisible and an empty list would read as a broken search. Positioned so the
                                    caption does not raise the row height. */}
                                {filters.createdAfterDate && filters.createdBeforeDate &&
                                    <div className={cx('tw-absolute tw-top-full tw-text-sm tw-text-default-dimmed', {
                                        '-lg:tw-hidden': !isSearchOpen
                                    })}
                                    >
                                        {`${filters.createdAfterDate.format(dateFormat)} — ${filters.createdBeforeDate.format(dateFormat)}`}
                                    </div>}
                            </div>
                            <FiltersDropdown
                                className='tw-ml-3 -lg:tw-order-3 tw-flex-nowrap'
                                refreshClassName='-lg:tw-hidden'
                                applyClassName='-lg:tw-hidden'
                                isLoading={loading}
                                applyFilters={applyFilters}
                                {...rest}
                            >
                                <Fragment>
                                    <DatePicker
                                        maxDate={filters.createdBeforeDate}
                                        value={filters.createdAfterDate}
                                        timeZone={timeZone}
                                        format={dateFormat}
                                        label={t(T_KEYS.FILTERS.CREATED_AFTER_DATE)}
                                        onChange={(value) => onFiltersChange({createdAfterDate: value})}
                                    />
                                    <DatePicker
                                        minDate={filters.createdAfterDate}
                                        value={filters.createdBeforeDate}
                                        timeZone={timeZone}
                                        format={dateFormat}
                                        label={t(T_KEYS.FILTERS.CREATED_BEFORE_DATE)}
                                        onChange={(value) => onFiltersChange({createdBeforeDate: value})}
                                    />
                                    <Select
                                        placeholder={t(T_KEYS.FILTERS.ENABLED)}
                                        options={enabledSelectOptions}
                                        noneOption
                                        valueAs='id'
                                        value={filters.enabled}
                                        onChange={(id) => onFiltersChange({enabled: id})}
                                    />
                                </Fragment>
                            </FiltersDropdown>
                        </Fragment>
                    )}
                </FiltersWrapper>

                {!isSearchOpen &&
                    <div className='tw-flex tw-ml-auto -lg:tw-order-1 -sm:tw-w-full'>
                        {hasSelection ?
                            selectionButtons
                            :
                            <Fragment>
                                <Dropdown
                                    className='-sm:tw-w-1/2'
                                    triggerNode={
                                        <Button width='small' icon='more-vertical' className='-sm:tw-w-full'>
                                            {t(T_KEYS.BUTTONS.ACTIONS)}
                                        </Button>
                                    }
                                >
                                    <DropdownItem className='tw-text-left' onClick={openImportFileInput}>
                                        {t(T_KEYS.BUTTONS.IMPORT_EXCEL)}
                                    </DropdownItem>
                                    <DropdownItem
                                        className='tw-text-left'
                                        disabled={downloadFileDisabled}
                                        onClick={handleDownloadImportTemplate}
                                    >
                                        {t(T_KEYS.BUTTONS.DOWNLOAD_IMPORT_TEMPLATE)}
                                    </DropdownItem>
                                    <DropdownItem className='tw-text-left' disabled={downloadFileDisabled} onClick={handleExportAll}>
                                        {t(T_KEYS.BUTTONS.EXPORT)}
                                    </DropdownItem>
                                    <DropdownItem className='tw-text-left' onClick={handleBulkFilteredDeleteClick}>
                                        {t(T_KEYS.BUTTONS.BULK_DELETE)}
                                    </DropdownItem>
                                </Dropdown>
                                <ActionButton width='small' action='create' className='tw-ml-2' onClick={handleCreateClick}>
                                    {t(T_KEYS.BUTTONS.CREATE)}
                                </ActionButton>
                            </Fragment>}
                    </div>}
            </div>

            {!isEmpty(connects) && <Pagination
                maxItemsToDisplay={9}
                className='tw-ml-auto tw-flex-1'
                itemsClassName='tw-min-w-64 tw-justify-between sm:tw-justify-end'
                rangeClassName='-sm:tw-hidden'
                pageSelectorClassName='-sm:tw-hidden'
                pageSize={pagination.pageSize}
                pageNumber={pagination.page}
                totalItems={totalCount}
                onPageNumberChange={setPage}
                onPageSizeChange={setPageSize}
            />}

            <div className={cx('lg:tw-hidden', {'tw-mb-10': hasSelection})}>
                <ClientConnectAccordion {...clientConnectViewProps} />
            </div>

            {hasSelection &&
                <div className='tw-flex tw-justify-center tw-fixed tw-left-0 tw-right-0 tw-z-1 tw-bottom-16 -lg-landscape:tw-bottom-10
                                tw-p-4 tw-bg-light tw-shadow-sm-top sm:tw-hidden'
                >
                    {selectionButtons}
                </div>}
            <div className='tw-overflow-hidden tw-overflow-x-auto -lg:tw-hidden'>
                <ClientConnectTable {...clientConnectViewProps} />
            </div>

            {formDialogProps && <ClientConnectFormDialog
                {...formDialogProps}
                groups={groups}
                users={agentUsers}
                onClose={handleFormDialogSubmit}
            />}

            <ClientConnectMessageDialog
                {...sendMessageDialogProps}
                onClose={handleCloseSendMessageDialog}
            />

            <ClientConnectScheduledMessagesDialog
                {...scheduledMessagesDialogProps}
                timeZone={timeZone}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                onDelete={handleDeleteScheduledMessageClick}
                onClose={onCloseClientConnectScheduleMessageClick}
            />

            <ConfirmModal
                {...deleteScheduledMessageConfirmationDialogProps}
                title={t(T_KEYS.CONFIRM_DELETE_SCHEDULED_MESSAGE_DIALOG.TITLE)}
                onClose={handleDeleteScheduledMessageConfirmationDialogClose}
            />

            <ConfirmModal
                open={deleteConfirmationDialogOpen}
                data={deleteCandidate}
                title={t(T_KEYS.CONFIRM_DELETE_DIALOG.TITLE)}
                onClose={handleDeleteConfirmationDialogClose}
            />

            <ConfirmModal
                open={bulkDeleteConfirmationDialogOpen}
                title={t(T_KEYS.CONFIRM_BULK_DELETE_DIALOG.TITLE)}
                onClose={handleBulkDeleteConfirmationDialogClose}
            >
                {renderConfirmBulkDeleteModalContent(selectedIds.length)}
            </ConfirmModal>
            <ConfirmModal
                open={bulkDeleteFileredConfirmationDialogOpen}
                title={t(T_KEYS.CONFIRM_BULK_DELETE_DIALOG.TITLE)}
                onClose={handleBulkDeleteFiltredItemsConfirmationDialogClose}
            >
                {renderConfirmBulkDeleteModalContent(totalCount)}
            </ConfirmModal>

            <ImportErrorsDialog
                open={importErrorsDialogOpen}
                errors={importErrors}
                importedConnects={importedConnects}
                getAssignmentUserInfoFields={getUserInfoFields}
                onClose={handleImportErrorsDialogClose}
            />

            <FileInput accept='.xls,.xlsx' setInputRef={setImportRef} onUpload={handleUploadImportFile} />
        </Fragment>
    );
};
